import React, { useEffect, useMemo, useState } from 'react'
import { Info } from '@mui/icons-material'
import { Box, Grid, LinearProgress, Stack, Tooltip } from '@mui/material'
import NormalText from 'common/Typography/NormalText'
import { format, fromUnixTime } from 'date-fns'
import {
  GetActiveUserPlansDetailsType,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
} from 'types/api/faber/oneTimePack.type'

import AddUserPlanDialog from '../dialogs/AddUserPlanDialog'

interface Props {
  activeOtpackUserPlans?: GetActiveUserPlansDetailsType[]
  allInactiveUserPlans?: GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  setOtpackDataRemaining: (value: number) => void
  userId: string
}
export default function CustomerPlansCard({
  allInactiveUserPlans,
  activeOtpackUserPlans,
  setOtpackDataRemaining,
  userId
}: Props) {
  const [totalActiveDataLeft, setTotalActiveDataLeft] = useState(0)
  const [totalInactiveData, setTotalInactiveData] = useState(0)

  const getActiveOtpUserPacks = useMemo(() => {
    if (activeOtpackUserPlans) {
      const getAllPlans = activeOtpackUserPlans.map((item) => (
        <Grid
          key={`active-${item.createdAt}+`}
          container
          sx={{
            justifyContent: 'space-between'
          }}
        >
          <Grid item xs={3.5}>
            <NormalText>{item.packageName}</NormalText>
          </Grid>
          <Grid item xs={3}>
            <Stack flexDirection={'row'} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: '40%', px: 1 }}>
                <LinearProgress
                  color='primary'
                  variant='determinate'
                  value={
                    (item.packageDataAllowed -
                      item.packageDataRemaining / item.packageDataAllowed) *
                    100
                  }
                />
              </Box>
              <Box sx={{ minWidth: '20%' }}>
                <NormalText variant='body3' sx={{ alignItems: 'center' }}>
                  {item.packageDataAllowed - item.packageDataRemaining}/
                  {item.packageDataAllowed} GB
                </NormalText>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={2}>
            <NormalText>
              {format(
                fromUnixTime(item.packagePurchased / 1000),
                'dd MMM yyyy HH:mm'
              )}
            </NormalText>
          </Grid>
          <Grid item xs={1.5}>
            {' '}
            <NormalText>{item.planStatus}</NormalText>
          </Grid>
          <Grid item xs={2}>
            <NormalText> {item.packageValidityInDays} days </NormalText>
          </Grid>
        </Grid>
      ))
      return getAllPlans
    }
  }, [activeOtpackUserPlans])

  const getAllInactiveAndTerminatedOtpackUserPlans = useMemo(() => {
    if (allInactiveUserPlans) {
      const getAllPlans = allInactiveUserPlans.map((item) => (
        <Grid
          key={`inactive-terminated-${item.createdAt}+`}
          container
          sx={{
            justifyContent: 'space-between'
          }}
        >
          <Grid item xs={3.5}>
            <Stack flexDirection={'row'} sx={{ alignItems: 'center' }}>
              <NormalText>{item.packageName}</NormalText>
              <Tooltip title={`Applied Plan Id: ${item.appliedPlanId}`}>
                <Info sx={{ color: 'lightgrey', fontSize: 'small' }} />
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack flexDirection={'row'} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: '40%', px: 1 }}>
                <LinearProgress
                  color='primary'
                  variant='determinate'
                  value={0}
                />
              </Box>
              <Box sx={{ minWidth: '20%' }}>
                <NormalText variant='body3' sx={{ alignItems: 'center' }}>
                  0/{item.packageDataAllowed} GB
                </NormalText>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={2}>
            <NormalText theme='muted'>
              {format(fromUnixTime(item.updatedAt / 1000), 'dd MMM yyyy HH:mm')}
            </NormalText>
          </Grid>
          <Grid item xs={1.5}>
            {' '}
            <NormalText theme='muted'>{item.planStatus}</NormalText>
          </Grid>
          <Grid item xs={2}>
            <NormalText theme='muted'>
              {' '}
              {item.packageValidityInDays} days{' '}
            </NormalText>
          </Grid>
        </Grid>
      ))
      return getAllPlans
    }
  }, [allInactiveUserPlans])

  useEffect(() => {
    if (activeOtpackUserPlans) {
      const activeTotalDataLeft = activeOtpackUserPlans.reduce(
        (accumulator, item) => accumulator + item.packageDataRemaining,
        0
      )
      setTotalActiveDataLeft(activeTotalDataLeft)
    }
    if (allInactiveUserPlans) {
      const inactiveData = allInactiveUserPlans.reduce(
        (accumulator, item) => accumulator + item.packageDataAllowed,
        0
      )
      setTotalInactiveData(inactiveData)
    }
  }, [allInactiveUserPlans, activeOtpackUserPlans])

  useEffect(() => {
    setOtpackDataRemaining(totalActiveDataLeft + totalInactiveData)
  }, [totalActiveDataLeft, totalInactiveData])

  return (
    <>
      <Stack>
        <Stack>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item>
              <NormalText isBold sx={{ fontSize: '21px' }}>
                Active/Inactive Plans
              </NormalText>
            </Grid>
            <Grid item>
              <AddUserPlanDialog userId={userId} />
            </Grid>
          </Grid>
          <NormalText theme='muted'>
            The current status of the customer&apos;s active/unused plan(s).
          </NormalText>
        </Stack>
        <Stack flexDirection={'row'}>
          <Grid container justifyContent='space-between'>
            <Grid item xs={3.5}>
              <NormalText theme='muted'>Plan</NormalText>
            </Grid>
            <Grid item xs={3}>
              <NormalText theme='muted'>Data Usage</NormalText>
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>Purchased</NormalText>{' '}
            </Grid>
            <Grid item xs={1.5}>
              <NormalText theme='muted'>Status</NormalText>{' '}
            </Grid>
            <Grid item xs={2}>
              <NormalText theme='muted'>Validity</NormalText>{' '}
            </Grid>
            {getActiveOtpUserPacks}
            {getAllInactiveAndTerminatedOtpackUserPlans}
          </Grid>
        </Stack>
      </Stack>
    </>
  )
}
